import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Container, Group, Loader, ScrollArea, Stack, Text } from '@mantine/core';
import { IconEye } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import {
  ActivityIndicator,
  ChatInput,
  ChatInputSkeleton,
  ConversationLengthNotice,
  deriveActivityLabel,
  ModeSelectionScreen,
  ThinkingIndicator,
  TurnGroup,
} from '~portal/components';
import { lengthAdvisoryDock } from '~portal/components/chat/blocks/ConversationLengthNotice.css';
import { ArtifactLineageProvider, useChatAccess } from '~portal/contexts';
import { getAppEnv } from '~portal/env';
import { useChatScroll, useConversation, useHideOnScroll } from '~portal/hooks';
import { chatContainerSize, logger } from '~portal/lib';
import { artifactLineageIndex } from '~portal/stores';
import { toAgentMode, useChatModeStore } from '~portal/stores/chat-mode-store';
import * as styles from './$conversationId.css';

export const Route = createFileRoute('/_protected/chat/$conversationId')({
  component: ChatPage,
});

function ChatPage() {
  const { conversationId } = Route.useParams();

  // The Charlie mode lives in the shared chat-mode store (selection screen +
  // composer dropdown); it maps onto the wire-level agent mode when prompting.
  const selectedMode = toAgentMode(useChatModeStore((state) => state.mode));
  // First-run mode selection: the empty state shows the picker cards until
  // the user confirms a mode at least once (Charlie rev 2, H2-181).
  const hasConfirmedSelection = useChatModeStore((state) => state.hasConfirmedSelection);

  // Use chat view hook for messaging, groups, and query helpers
  const { sendMessage, isSubmitting, groups, isLoadingState, conversation } = useConversation(
    conversationId,
    selectedMode,
  );

  // Sticks to the bottom while a turn arrives and releases when the reader scrolls up; see
  // useChatScroll for what this replaced and why a height observer beats a change counter. An
  // empty transcript (the first-run mode picker) is left at the top instead.
  const { scrollRef, contentRef } = useChatScroll(conversationId, groups.length === 0);

  // H2-234 — on a phone on its side the top chrome lies over the transcript; it steps aside
  // while the reader scrolls down and returns on any scroll up. Inert in every other viewport.
  useHideOnScroll(scrollRef, conversationId);

  /**
   * The nudge is minted once per conversation by the backend and rides on the turn that crossed
   * the threshold; docked, it is a property of the conversation rather than of that turn. Dismissal
   * is deliberately local and unpersisted: it silences the card for this visit, and a reader who
   * comes back to a conversation that is still long is told again, once. Held as the id it was
   * dismissed for, not a flag: this page stays mounted when the sidebar switches conversation
   * (only the `$conversationId` param changes), and a flag silenced the next conversation's too.
   */
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  const advisoryDismissed = dismissedFor === conversationId;
  const showLengthAdvisory =
    !advisoryDismissed && !isLoadingState && groups.some((group) => group.lengthAdvisory);

  // Read-only state comes from the shared chat-access context (backend-stamped),
  // so the layout and this page agree without re-deriving ownership.
  const { isReadOnly } = useChatAccess();

  /**
   * Artefact lineage across the whole conversation, folded once per render of the transcript.
   *
   * Each turn's gallery shows what that turn delivered; this is only how a tile learns that its
   * deliverable moved on later ("Newer version (v3) below") and what a provenance pointer names.
   * Built here rather than inside TurnGroup because a turn cannot see its successors.
   */
  const lineage = useMemo(() => artifactLineageIndex(groups), [groups]);

  // Check if the agent is thinking or warming up (last group flags)
  const lastGroup = groups.length > 0 ? groups[groups.length - 1] : undefined;
  const isThinking = lastGroup?.isThinking ?? false;
  const isWarmingUp = lastGroup?.isWarmingUp ?? false;

  /**
   * `VITE_RENDER_TASK_DETAILS` splits the two readings of a turn in flight. With details on,
   * the developer gets today's tool trace (rendered by TurnGroup) plus the plain "Thinking…"
   * placeholder; with them off, a user gets one self-replacing line saying what the agent is
   * doing — and nothing else, so the trace and the summary of it are never on screen together.
   */
  const showTaskDetails = getAppEnv().render.taskDetails;

  /**
   * The activity label is derived from the turn's raw events rather than from its grouped view:
   * `collectEventGroups` keeps what the transcript needs to *render*, which is a strict subset
   * of what the label needs to be *decided* (the phase signals — ThinkingStart, Summarisation,
   * TurnComplete — survive only as flags, or not at all). Both projections come off this same
   * array, and it is the array the store persists, so a live turn and one reloaded mid-flight
   * see identical input and resolve to identical labels.
   */
  const turnEvents = lastGroup
    ? (conversation ?? []).filter((evt) => evt.metadata.turnId === lastGroup.turnId)
    : [];
  const activity = showTaskDetails ? null : deriveActivityLabel(turnEvents);
  // A live run is the liveness authority; the derived label goes quiet of its own accord at
  // TurnComplete. Both are required so a turn whose process died mid-flight — no terminal event
  // ever written — does not leave a spinner running for the next reader of that conversation.
  const showActivity = !!activity && isSubmitting && !lastGroup?.error;

  // console.debug('render groups:', groups);

  useEffect(() => {
    logger.info('conversation viewed', { conversationId });
  }, [conversationId]);

  // Past the first-run selection, every NEW chat starts back in Answer mode —
  // Analyse is a per-chat opt-in via the composer dropdown (operator spec,
  // 2026-08-25). Deps deliberately exclude the confirmation flag: the reset
  // applies on ENTERING a new chat, never when the first-run picker's own
  // confirmation lands (that choice governs the chat it was made in).
  useEffect(() => {
    const store = useChatModeStore.getState();
    if (conversationId === 'new' && store.hasConfirmedSelection) store.setMode('answer');
  }, [conversationId]);

  return (
    // The lineage index is provided, not only passed to TurnGroup: an inline `artifact:<id>`
    // chip inside the answer's prose needs the same file names, and it is created by
    // react-markdown several layers down where no prop reaches (ADR-21).
    <ArtifactLineageProvider lineage={lineage}>
      <Stack className={styles.pageContainer}>
        {/* Read-only banner shown when an admin views another user's conversation */}
        {isReadOnly && (
          <Alert icon={<IconEye size={18} />} color="yellow" variant="light" radius={0} py="xs">
            <Text size="sm">Read-only — admin view of another user’s conversation</Text>
          </Alert>
        )}

        {/* Chat messages area */}
        <ScrollArea
          // The padding is in the class, not a `p` prop — see `messagesScrollArea.root`.
          viewportRef={scrollRef}
          className={styles.messagesScrollArea.root}
          classNames={{
            viewport: styles.messagesScrollArea.viewport,
            content: styles.messagesScrollArea.content,
          }}
        >
          {/* `w="100%"` is load-bearing, not decoration: the ScrollArea's content element is a
            flex column (see $conversationId.css.ts), and a flex item whose cross-axis margins
            are `auto` — which is exactly what Mantine's Container uses to centre itself — is
            never stretched by `align-items: stretch`. Without an explicit width the column
            therefore shrink-wrapped its widest message (~412px of a 720px allowance) and sat
            off-centre from the composer below. */}
          <Container size={chatContainerSize} w="100%" ref={contentRef}>
            <Stack gap="md">
              {groups.length === 0 ? (
                isLoadingState ? (
                  /* The same spinner treatment the running turn uses (ActivityIndicator), with no
                   label: a fetch that resolves in a few hundred ms does not need narrating, and
                   a sentence here would be a second thing to read and then unread. */
                  <Group justify="center" py="xl" role="status" aria-label="Loading conversation">
                    <Loader size="sm" />
                  </Group>
                ) : !hasConfirmedSelection ? (
                  <ModeSelectionScreen />
                ) : (
                  <Text c="dimmed" ta="center" py="xl">
                    Start a conversation by sending a message below
                  </Text>
                )
              ) : (
                /* Fade the transcript in rather than letting it replace the spinner in one frame.
                 Mount-driven and pure CSS, so it plays exactly once per conversation and is a
                 no-op under prefers-reduced-motion. */
                <Box className={styles.transcriptReveal}>
                  {groups.map((group, idx) => (
                    <TurnGroup
                      key={group.turnId}
                      group={group}
                      isLatest={idx === groups.length - 1}
                      isActive={isSubmitting && idx === groups.length - 1}
                      lineage={lineage}
                      // Owners rate their own responses; a read-only (admin) viewer, or a chat that
                      // has not been persisted yet, gets no thumbs.
                      feedbackConversationId={
                        !isReadOnly && conversationId !== 'new' ? conversationId : undefined
                      }
                    />
                  ))}
                  {showTaskDetails ? (
                    <>
                      {/* Show thinking indicator when agent is waiting for LLM response. The count
                        runs from the turn's own start, so a reload mid-turn reports the real wait. */}
                      {isThinking && <ThinkingIndicator startedAt={lastGroup?.tStart} />}
                      {/* Show warming indicator when ClickHouse is having consecutive timeouts */}
                      {isWarmingUp && (
                        <Group gap="xs" justify="center" py="sm">
                          <Loader size="sm" color="orange" />
                          <Text c="orange" fs="italic" size="sm">
                            Warming up database...
                          </Text>
                        </Group>
                      )}
                    </>
                  ) : (
                    /* One line for the whole turn. A cold database is one of its labels rather
                     than a second row, so the user is never told two things at once. */
                    showActivity && (
                      <ActivityIndicator label={activity} startedAt={lastGroup?.tStart} />
                    )
                  )}
                </Box>
              )}
            </Stack>
          </Container>
        </ScrollArea>

        {/* Chat input area — always present except in read-only admin views. While a conversation
          is being fetched it is a skeleton of the same box rather than nothing at all: withholding
          it made the composer pop in on arrival and shove the transcript up by its own height. */}
        {!isReadOnly && (
          <Box
            // The padding is in the class, not a `p` prop — see `composerArea`.
            className={styles.composerArea}
            // todo: wip layout init transitions
            // className={groups.length === 0 ? styles.inputAreaEmpty : styles.inputAreaWithMessages}
          >
            {/* The length nudge lives here rather than in the transcript: it is about what to
              do next, so it sits with the composer, stays put while the reader scrolls back, and
              takes its space from the scroll area instead of from an answer. */}
            {showLengthAdvisory && (
              // The same Container the composer uses, so the banner is exactly the pill's width
              // — a wider banner over a narrower pill reads as two unrelated things.
              <Container size={chatContainerSize} w="100%" className={lengthAdvisoryDock}>
                <ConversationLengthNotice onDismiss={() => setDismissedFor(conversationId)} />
              </Container>
            )}
            {isLoadingState ? (
              <ChatInputSkeleton />
            ) : (
              /* Mode is picked from the composer's dropdown (chat-mode store); the
               backend fixes it against the conversation on the first prompt. */
              <ChatInput
                onSubmit={sendMessage}
                disabled={isSubmitting}
                hasMessages={groups.length > 0}
                isNewChat={conversationId === 'new'}
              />
            )}
          </Box>
        )}
      </Stack>
    </ArtifactLineageProvider>
  );
}
